import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { authAPI } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { User, Shield, Palette, Sun, Moon, Check } from 'lucide-react';

const Profile = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const { theme, primaryColor, updateTheme, updatePrimaryColor } = useTheme();

  const handle2FAEnable = async () => {
    setLoading(true);
    try {
      const response = await authAPI.enable2FA();
      toast.success('2FA Secret Generated. Use your authenticator app to scan the QR code.');
      toast.info(`Secret: ${response.data.secret}`);
    } catch (error) {
      toast.error('Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const colorPresets = [
    { name: 'Green', value: '#22c55e' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Teal', value: '#14b8a6' },
    { name: 'Indigo', value: '#6366f1' },
  ];

  const handleThemeChange = (newTheme) => {
    updateTheme(newTheme);
    toast.success(`Theme changed to ${newTheme} mode`);
  };

  const handleColorChange = (color) => {
    updatePrimaryColor(color);
    toast.success('Primary color updated');
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="profile-page">
      <div>
        <h1 className="text-4xl font-bold text-black mb-2">Profile Settings</h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card className="glass-card p-6">
          <h2 className="text-xl font-bold text-black mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Personal Information
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-gray-600 text-sm">Full Name</p>
              <p className="text-black">{user?.full_name}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Email</p>
              <p className="text-black">{user?.email}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Phone</p>
              <p className="text-black">{user?.phone || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-gray-600 text-sm">Role</p>
              <p className="text-black capitalize">{user?.role}</p>
            </div>
          </div>
        </Card>

        {/* Security */}
        <Card className="glass-card p-6">
          <h2 className="text-xl font-bold text-black mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Security
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-gray-600 text-sm mb-2">Two-Factor Authentication</p>
              <p className="text-black mb-3">
                Status: {user?.is_2fa_enabled ? (
                  <span className="text-green-400 font-semibold">Enabled ✓</span>
                ) : (
                  <span className="text-yellow-400 font-semibold">Disabled</span>
                )}
              </p>
              {!user?.is_2fa_enabled && (
                <Button onClick={handle2FAEnable} disabled={loading} className="btn-primary" data-testid="enable-2fa-button">
                  {loading ? <div className="loading-spinner"></div> : 'Enable 2FA'}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Theme & Appearance */}
      <Card className="glass-card p-6">
        <h2 className="text-xl font-bold text-black mb-6 flex items-center">
          <Palette className="w-5 h-5 mr-2" />
          Theme & Appearance
        </h2>
        
        <div className="space-y-8">
          {/* Theme Mode */}
          <div>
            <Label className="text-black text-base font-semibold mb-4 block">Theme Mode</Label>
            <div className="flex gap-4">
              <button
                onClick={() => handleThemeChange('light')}
                className={`flex-1 p-6 rounded-xl border-2 transition-all duration-300 ${
                  theme === 'light' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid="theme-light-btn"
              >
                <div className="flex items-center justify-center mb-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    theme === 'light' ? 'bg-primary' : 'bg-white/10'
                  }`}>
                    <Sun className={`w-8 h-8 ${theme === 'light' ? 'text-black' : 'text-gray-600'}`} />
                  </div>
                </div>
                <p className="text-black font-medium text-center mb-1">Light Mode</p>
                <p className="text-gray-600 text-sm text-center">Bright and clean</p>
                {theme === 'light' && (
                  <div className="flex items-center justify-center mt-3">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                )}
              </button>

              <button
                onClick={() => handleThemeChange('dark')}
                className={`flex-1 p-6 rounded-xl border-2 transition-all duration-300 ${
                  theme === 'dark' 
                    ? 'border-primary bg-primary/10' 
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid="theme-dark-btn"
              >
                <div className="flex items-center justify-center mb-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    theme === 'dark' ? 'bg-primary' : 'bg-white/10'
                  }`}>
                    <Moon className={`w-8 h-8 ${theme === 'dark' ? 'text-black' : 'text-gray-600'}`} />
                  </div>
                </div>
                <p className="text-black font-medium text-center mb-1">Dark Mode</p>
                <p className="text-gray-600 text-sm text-center">Easy on the eyes</p>
                {theme === 'dark' && (
                  <div className="flex items-center justify-center mt-3">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Primary Color */}
          <div>
            <Label className="text-black text-base font-semibold mb-4 block">Primary Color</Label>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3 mb-4">
              {colorPresets.map(color => (
                <button
                  key={color.value}
                  onClick={() => handleColorChange(color.value)}
                  className={`h-14 rounded-lg transition-all duration-300 hover:scale-110 relative ${
                    primaryColor === color.value ? 'ring-4 ring-white ring-offset-4 ring-offset-background scale-110' : ''
                  }`}
                  style={{ backgroundColor: color.value }}
                  data-testid={`color-${color.name.toLowerCase()}`}
                  title={color.name}
                >
                  {primaryColor === color.value && (
                    <Check className="w-6 h-6 text-black absolute inset-0 m-auto" />
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg">
              <Label className="text-gray-600">Custom Color:</Label>
              <input
                type="color"
                value={primaryColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="h-12 w-24 rounded cursor-pointer border-2 border-white/10"
                data-testid="custom-color-picker"
              />
              <span className="text-black font-mono">{primaryColor}</span>
            </div>
          </div>

          {/* Preview */}
          <div className="p-6 bg-white/5 rounded-lg border border-white/10">
            <p className="text-gray-600 text-sm mb-3">Preview:</p>
            <div className="space-y-3">
              <Button className="btn-primary">Primary Button</Button>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                <span className="text-black">Accent Color</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
            <p className="text-blue-400 text-sm">
              💡 <strong>Tip:</strong> Changes are applied instantly and saved to your account automatically. Refresh the page to see your theme persist!
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Profile;
